import {
  type GetServerSidePropsContext,
  type GetStaticPropsContext,
} from 'next'
import { type ParsedUrlQuery } from 'querystring'

import { type Locale, defaultLocale } from './language'

export type UrlParameters = Record<string, string>

export type Slugs = string | string[] | null

export interface PageUrlOptions {
  newLocale?: Locale
  locale?: Locale
  parameters?: Record<string, string>
}

export type PageType =
  | 'accountAddressPage'
  | 'accountPage'
  | 'blogAuthor'
  | 'blogCategory'
  | 'blogPage'
  | 'blogPost'
  | 'cartPage'
  | 'collection'
  | 'collectionChild'
  | 'error404Page'
  | 'error500Page'
  | 'homePage'
  | 'loginPage'
  | 'logoutPage'
  | 'page'
  | 'passwordRecoveryPage'
  | 'product'
  | 'productCombinedListing'
  | 'shopPage'
  | 'shopPageChild'
  | 'shopSalePage'
  | 'shopSalePageChild'
  | 'shopSearchPage'
  | 'signupPage'
  | 'sitePasswordPage'

export type SitemapPageType =
  | 'blogAuthor'
  | 'blogCategory'
  | 'blogPage'
  | 'blogPost'
  | 'cartPage'
  | 'collection'
  | 'collectionChild'
  | 'homePage'
  | 'loginPage'
  | 'page'
  | 'passwordRecoveryPage'
  | 'product'
  | 'shopPage'
  | 'shopPageChild'
  | 'shopSalePage'
  | 'shopSalePageChild'
  | 'shopSearchPage'
  | 'signupPage'

export interface CustomStaticPropsContext<
  Q extends ParsedUrlQuery = ParsedUrlQuery,
> extends GetStaticPropsContext<Q> {
  locale: Locale
  locales: Locale[]
}

export interface CustomErrorStaticPropsContext<
  Q extends ParsedUrlQuery = ParsedUrlQuery,
> extends GetStaticPropsContext<Q> {
  locale?: Locale
  locales: Locale[]
}

export interface CustomServerSidePropsContext<
  Q extends ParsedUrlQuery = ParsedUrlQuery,
> extends GetServerSidePropsContext<Q> {
  locale: Locale
  locales: Locale[]
}

export interface StaticPathWithSlug {
  params: {
    slug: string
  }
  locale: Locale
}

export interface StaticPathWithSlugs {
  params: {
    slugs: string[]
  }
  locale: Locale
}

export const pageRoutes: Record<PageType, string[]> = {
  accountAddressPage: ['account', 'addresses'],
  accountPage: ['account'],
  blogAuthor: ['journal', 'author', '[author_slug]'],
  blogCategory: ['journal', 'category', '[category_slug]'],
  blogPage: ['journal'],
  blogPost: ['journal', '[post_slug]'],
  cartPage: ['cart'],
  collection: ['shop', '[collection_slug]'],
  collectionChild: ['shop', '[collection_slug]', '[child_collection_slug]'],
  error404Page: ['404'],
  error500Page: ['500'],
  homePage: [],
  loginPage: ['login'],
  logoutPage: ['api', 'auth', 'logout'],
  page: ['[page_slug]'],
  passwordRecoveryPage: ['password-recovery'],
  product: ['products', '[product_slug]'],
  productCombinedListing: ['products', '[product_slug]'],
  shopPage: ['shop'],
  shopPageChild: ['shop', 'all', '[child_collection_slug]'],
  shopSalePage: ['shop', 'sale'],
  shopSalePageChild: ['shop', 'sale', '[child_collection_slug]'],
  shopSearchPage: ['shop', 'search'],
  signupPage: ['signup'],
  sitePasswordPage: ['site-password'],
}

/**
 * Determines if page type has product collection.
 */
export const isRouteCollection = (pageType: PageType) => {
  const collectionPageTypes: PageType[] = [
    'collection',
    'collectionChild',
    'shopPage',
    'shopPageChild',
    'shopSalePage',
    'shopSalePageChild',
  ]

  return collectionPageTypes.includes(pageType)
}

const getUrlSegments = (
  pageType: PageType,
  slugs: string | string[] | null = null,
) => {
  const urlSlugs = slugs && Array.isArray(slugs) ? slugs : [slugs]
  let slugIndex = 0

  return (
    pageRoutes[pageType]?.map((segment) => {
      // Replace segment placeholder with a slug or an empty string
      if (/\[.*\]/.test(segment)) {
        slugIndex += 1
        return urlSlugs?.[slugIndex - 1] ?? ''
      }

      return segment
    }) ?? []
  )
}

const getUrlParameterString = (parameters?: Record<string, string>) => {
  const searchParams = new URLSearchParams()

  Object.entries(parameters ?? {}).forEach((parameter) => {
    if (parameter[0] && parameter[1]) {
      searchParams.set(parameter[0], parameter[1].replace(/\s*,\s*/g, ','))
    }
  })

  return decodeURIComponent(searchParams.toString())
}

/**
 * Gets relative page URL for Next.js links without locale segment.
 */
export const getLinkPageUrl = (
  pageType: PageType,
  slugs?: Slugs,
  parameters?: UrlParameters,
) => {
  const pageUrlSegments = getUrlSegments(pageType, slugs)
  const url = pageUrlSegments.filter(Boolean).join('/')

  const urlParameters = getUrlParameterString(parameters)
  const urlWithParameters = [url, urlParameters].filter(Boolean).join('?')

  return `/${urlWithParameters}`
}

/**
 * Gets relative page URL.
 */
export const getPageUrl = (
  locale: Locale,
  pageType: PageType,
  slugs?: Slugs,
  parameters?: UrlParameters,
) => {
  const localeSegment = locale !== defaultLocale ? `${locale}` : null
  const pageUrlSegments = getUrlSegments(pageType, slugs)
  const url = [localeSegment, ...pageUrlSegments].filter(Boolean).join('/')

  const urlParameters = getUrlParameterString(parameters)
  const urlWithParameters = [url, urlParameters].filter(Boolean).join('?')

  return `/${urlWithParameters}`
}

/**
 * Gets formatted query string.
 */
export const getFormattedQueryString = (queryString?: string) => {
  if (!queryString) {
    return ''
  }

  let formattedQueryString = `${queryString}`

  if (formattedQueryString.startsWith('?')) {
    formattedQueryString = formattedQueryString.slice(1)
  }

  return `?${formattedQueryString}`
}

/**
 * Gets page URL without hash from document.location.
 */
export const getPageLocation = () => {
  return (
    document.location.protocol +
    '//' +
    document.location.hostname +
    document.location.pathname +
    document.location.search
  )
}
